import React, {Component, useEffect, useState} from 'react';
import './App.css';
import {Alert, AutoComplete, Breadcrumb, Button, Input, Layout, Spin} from "antd";
import VisualXPathEditor from "./components/visualXPathEditor/visualXPathEditor";
import {getSchemas, listPageTypes, ObjectSchema, PageType, ParserConfig, ParserField} from "./api/config";
import {RightColumn} from "./containers/right_column/RightColumn";
import {LeftColumn, SelectedItem} from "./containers/left_column/LeftColumn";
import {findProxy} from "./api/iframe";
import {getCrawlPlan, PlanNode} from "./api/crawler";
import {LoadingOutlined} from '@ant-design/icons';

const antIcon = <LoadingOutlined style={{fontSize: 24}} spin/>;

const {Content} = Layout;

function getDefaultConfig(schema: ObjectSchema): ParserConfig {
    const ret: Record<string, ParserField> = {}

    for (const fieldName in schema.fields) {
        ret[fieldName] = {
            xpath: [],
            type: schema.fields[fieldName].type,

            // postprocessing
            value_empty_behavior: schema.fields[fieldName].value_empty_behavior
        }
    }

    return {
        item_block_xpath: null,
        fields: ret,
    }
}

interface AppState {
    crawlTree: PlanNode[]

    currentPageType: PageType | null
    pageTypes: Record<number, PageType>

    currentSchema: ObjectSchema | null
    schemas: Record<string, ObjectSchema>

    currentConfig: ParserConfig | null

    // selection
    exampleUrls: string[]
    currentURL: string | null
    currentProxy: string | null

    // status
    error: string | null
    loading: boolean
    findProxyInProgress: boolean
}

interface AppProps {
    siteID: number
    location: Location
}

export class App extends Component<AppProps, AppState> {
    constructor(props: any) {
        super(props);

        this.state = {
            crawlTree: [],

            currentPageType: null,
            pageTypes: {},

            currentURL: null,
            currentProxy: "",

            currentSchema: null,
            schemas: {},

            currentConfig: null,

            exampleUrls: [],

            error: null,
            loading: false,
            findProxyInProgress: false,
        }
    }

    async componentDidMount() {
        const newState: AppState = {...this.state}

        let results
        try {
            results = await Promise.all([
                getSchemas(this.props.siteID),
                getCrawlPlan(this.props.siteID),
                listPageTypes(this.props.siteID),
            ])
        } catch (e: any) {
            this.setState({
                error: `${e}`
            })
            return
        }
        newState.schemas = results[0]
        newState.crawlTree = results[1]
        newState.pageTypes = results[2]

        const searchParams = new URLSearchParams(this.props.location.search);
        const url = searchParams.get("url");
        if (url) {
            newState.currentURL = url;
        }

        const schemaSlug = searchParams.get("schema")
        if (schemaSlug) {
            newState.currentSchema = newState.schemas[schemaSlug];
        }

        this.setState(newState);
    }

    async navigate(url: string, proxy: string) {
        if (url == this.state.currentURL) {
            this.setState({
                loading: false
            })
            return
        }

        this.setState({
            loading: true,
        })

        if (!proxy) {
            this.setState({
                findProxyInProgress: true
            })
            const res = await findProxy(url);
            this.setState({
                findProxyInProgress: false
            })
            if (!res.proxy) {
                alert("Cant find proxy");
                return
            }

            proxy = res.proxy
        }

        this.setState({
            currentProxy: proxy,
            currentURL: url
        })
    }

    setConfig(config: ParserConfig) {
        this.setState({
            currentConfig: config,
        })
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({
            error: `${error.name}: ${error.message}`
        })
    }

    async selectPlanItem(selection: SelectedItem) {
        let schemaSlug = selection.schemaSlug
        if (!schemaSlug) {
            schemaSlug = "crawl"
        }

        let schema: ObjectSchema | null
        if (schemaSlug == "crawl" && selection.crawlConfigSchema) {
            // По возможности берем схему из плана обхода, т.к. планировщик может запретить некоторые переходы
            schema = selection.crawlConfigSchema
        } else {
            schema = this.state.schemas[schemaSlug]
        }

        let pageType: PageType | null = null
        if (selection.pageTypeID) {
            pageType = this.state.pageTypes[selection.pageTypeID];
        }


        const navigation: Record<string, any> = {}
        const url = selection.urls[0]
        if (url != this.state.currentURL) {
            this.setState({
                loading: true,
            })

            let proxy = this.state.currentProxy;
            if (!proxy) {
                this.setState({
                    findProxyInProgress: true
                })
                const res = await findProxy(url);
                this.setState({
                    findProxyInProgress: false
                })

                if (!res.proxy) {
                    alert("Cant find proxy");
                    return
                }

                proxy = res.proxy
            }
            navigation['currentURL'] = url
            navigation['currentProxy'] = proxy
        }

        this.setState({
            currentPageType: pageType,

            currentSchema: schema,
            currentConfig: pageType?.configs[schema.slug] || getDefaultConfig(schema),
            exampleUrls: selection.urls,

            ...navigation
        })
    }


    render() {
        return (
            <Layout>
                <Content style={{
                    // padding: 24,
                    margin: 0,
                    minHeight: 280,
                }}>
                    {
                        this.state.error &&
                        <Alert
                            message="Произошла ошибка"
                            description={`${this.state.error}`}
                            type="error"
                            showIcon
                        />
                    }

                    <Spin spinning={this.state.findProxyInProgress} size={"large"} tip="Поиск прокси"
                          indicator={antIcon}>
                        <Layout>
                            <LeftColumn siteID={this.props.siteID} objectSchemas={this.state.schemas}
                                        crawlPlan={this.state.crawlTree}
                                        onSelect={(s) => this.selectPlanItem(s)}
                                        pageTypes={Object.values(this.state.pageTypes)}
                                        onReloadCrawlPlan={() => {
                                            getCrawlPlan(this.props.siteID).then((crawlPlan) => this.setState({
                                                crawlTree: crawlPlan
                                            }))
                                        }}
                            />


                            <Content style={{
                                padding: "5px",
                                margin: 0,
                                minHeight: 280,
                            }}>
                                <Breadcrumb style={{margin: '16px 0'}}>
                                    <Breadcrumb.Item>Отзовики</Breadcrumb.Item>
                                    <Breadcrumb.Item>{this.state.currentPageType?.site_name}</Breadcrumb.Item>
                                    <Breadcrumb.Item>{this.state.currentPageType?.name}</Breadcrumb.Item>
                                    <Breadcrumb.Item>{this.state.currentSchema?.name}</Breadcrumb.Item>
                                </Breadcrumb>

                                <SearchBox value={this.state.currentURL || ""} proxy={this.state.currentProxy || ""}
                                           urls={this.state.exampleUrls}
                                           loading={this.state.loading} onSubmit={(val, proxy) => {
                                    this.setState({
                                            loading: true,
                                        }, () => this.navigate(val, proxy)
                                    )
                                }}/>
                                {
                                    (this.state.currentSchema) &&
                                    <VisualXPathEditor
                                        url={this.state.currentURL!}
                                        config={this.state.currentConfig}
                                        proxy={this.state.currentProxy!}
                                        schema={this.state.currentSchema}
                                        onConfigChanged={(data, reason) => {
                                            console.log(`Config changed by visual editor: ${reason}`)
                                            this.setConfig(data)
                                        }}
                                        loading={this.state.loading}
                                        onLoaded={() => {
                                            this.setState({loading: false})
                                        }}
                                    />
                                }
                            </Content>

                            {
                                this.state.currentPageType && <RightColumn
                                    pageTypeID={this.state.currentPageType.id}
                                    config={this.state.currentConfig}
                                    schema={this.state.currentSchema}
                                    currentURL={this.state.currentURL}
                                    proxy={this.state.currentProxy}
                                    onConfigChanged={(config) => this.setConfig(config)}
                                    onSave={() => {
                                        getCrawlPlan(this.props.siteID).then((crawlPlan) => this.setState({
                                            crawlTree: crawlPlan
                                        }))
                                        listPageTypes(this.props.siteID).then((pageTypes) => this.setState({
                                            pageTypes: pageTypes
                                        }))
                                    }}
                                />
                            }
                        </Layout>

                    </Spin>
                </Content>


            </Layout>
        );
    }
}

interface SearchBoxProps {
    value: string
    urls: string[]
    proxy: string
    loading: boolean
    onSubmit: (val: string, proxy: string) => void
}

function SearchBox({value, urls, proxy, loading, onSubmit}: SearchBoxProps) {
    const [val, setVal] = useState(value);
    const [proxyVal, setProxyVal] = useState(proxy);

    useEffect(() => {
        setVal(value);
    }, [value])

    useEffect(() => {
        setProxyVal(proxy);
    }, [proxy])
    return <>
        <Input
            disabled={loading} addonBefore={"proxy"} value={proxyVal} onChange={(v) => setProxyVal(v.target.value)}
            style={{marginBottom: "10px"}}/>

        <Input.Group compact>
            <AutoComplete
                disabled={loading}
                style={{width: 'calc(100% - 100px)'}}
                options={urls.map((url) => {
                    return {value: url}
                })}
                onChange={(v) => setVal(v)}
                value={val}
                onSelect={(v: any) => {
                    setVal(v);
                    onSubmit(v, proxyVal);
                }}
            />
            <Button loading={loading} style={{width: '100px'}} type="primary"
                    onClick={() => onSubmit(val, proxyVal)}>GO</Button>
        </Input.Group>
    </>
}

export default App;
